/** @format */

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import {
  Form,
  FormItem,
  Select,
  Option,
  Input,
  _$,
  Timepicker,
  Icon,
  Tooltip,
  Table
} from 'kpc-react'
import styled from 'styled-components'
import { ProductItem } from 'src/utils/interface'
import { cloneDeep } from 'lodash'
import {
  TriggerRulesProps,
  periodOption,
  methodOption,
  unitOption,
  pointsOption,
  intervalOption,
  countOption,
  initRuleItem,
  withCustomRule,
  handleDataUnitsValue,
  thanLastPeriod
} from './constants'
import { mbpsproduct } from '../index'
import ApiCommon from 'src/api/ApiCommon'
import { MetricItem } from 'src/utils/interface'
import { request } from 'src/utils'

interface AlarmRuleProps {
  className?: string
  currentProduct?: ProductItem
  triggerRules?: TriggerRulesProps[]
  setTriggerRules?: (value: TriggerRulesProps[]) => void
}
export interface AlarmRuleHandle {
  getTriggerRules: () => Promise<TriggerRulesProps[]>
}

const AlarmRule = forwardRef<AlarmRuleHandle, AlarmRuleProps>(
  ({ className, triggerRules, setTriggerRules, currentProduct }, ref) => {
    const formRef = useRef(null)
    // 监控项List
    const [metricList, setMetricList] = useState<MetricItem[]>([])
    useEffect(() => {
      if (currentProduct?.productType !== null && currentProduct?.productType !== undefined) {
        getProductMetricList()
      } else {
        setMetricList([])
      }
    }, [currentProduct])

    // 获取监控项
    const getProductMetricList = async () => {
      const { data } = await request(ApiCommon.getProductMetricList, {
        data: { ProductType: currentProduct?.productType, PageIndex: 1 }
      })
      if (data?.metricList?.length) {
        if (!mbpsproduct.includes(currentProduct?.productType)) {
          const map = data?.metricList.map(item => {
            const { units } = item
            return {
              ...item,
              units: units === 'bps' || units === 'Kpbs' ? 'Mbps' : units
            }
          })
          setMetricList(map)
        } else {
          setMetricList(data?.metricList)
        }
      } else {
        setMetricList([])
      }
      if (triggerRules[0]?.itemKey) {
        const update = triggerRules.map(item => {
          const { itemKey, triggerValue, units } = item
          if (itemKey && triggerValue && units) {
            const calc = handleDataUnitsValue(triggerValue, units)
            return {
              ...item,
              ...calc
            }
          } else {
            return { ...item }
          }
        })
        setTriggerRules(update)
      }
    }

    // 修改告警规则
    const changeRules = (index, value) => {
      const update = cloneDeep(triggerRules)
      update[index] = { ...update[index], ...value }
      setTriggerRules(update)
    }

    // 添加告警规则
    const addTriggerRules = async () => {
      const valid = await formRef.current.validate()
      if (valid) {
        const add = cloneDeep(triggerRules)
        add.push(initRuleItem)
        setTriggerRules(add)
      }
    }

    // 删除告警规则
    const removeTriggerRules = index => {
      const remove = cloneDeep(triggerRules)
      remove.splice(index, 1)
      setTriggerRules(remove)
    }

    useImperativeHandle(ref, () => ({
      getTriggerRules: async () => {
        const valid = await formRef.current.validate()
        return valid ? triggerRules : []
      }
    }))

    const scheme = {
      itemName: {
        title: _$('监控项名称'),
        width: 300,
        fixed: 'left',
        template({ itemKey, itemName }, index) {
          return (
            <FormItem
              className="table-space"
              hideLabel={true}
              value={itemKey && itemName ? `${itemKey}--${itemName}` : ''}
              rules={{ required: true }}
            >
              <Select
                width={255}
                searchable
                container=".select_position"
                value={itemKey && itemName ? `${itemKey}--${itemName}` : ''}
                onChange={value => {
                  const split = value.split('--')
                  const find = metricList.find(
                    item => item.metricKey === split[0] && item.metricName === split[1]
                  )
                  if (find) {
                    changeRules(index, {
                      itemKey: find.metricKey,
                      itemName: find.metricName,
                      units: find.units
                    })
                  }
                }}
              >
                {metricList?.length
                  ? metricList.map(item => (
                      <Option key={item.metricKey} value={`${item.metricKey}--${item.metricName}`}>
                        {item.metricName}
                      </Option>
                    ))
                  : ''}
              </Select>
              {itemName === '可用内存' || itemName === 'memory availability' ? (
                <Tooltip
                  content={_$(
                    // eslint-disable-next-line max-len
                    '从/proc/meminfo中得到MemAvailable，若/proc/meminfo中无MemAvailable，则MemAvailable=MemFree+Buffers+Cached'
                  )}
                >
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              ) : null}
              {itemName === '内存可用率' || itemName === 'available memory' ? (
                <Tooltip content={_$('可用内存/总内存*100%')}>
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              ) : null}
            </FormItem>
          )
        }
      },
      period: {
        title: _$('统计周期'),
        width: 150,
        template({ itemKey, period, compare }, index) {
          return !withCustomRule.includes(itemKey) ? (
            <FormItem hideLabel={true} value={period || ''} rules={{ required: true }}>
              <Select
                width={130}
                value={period || ''}
                container=".select_position"
                onChange={value => {
                  changeRules(index, {
                    period: value
                  })
                }}
              >
                {periodOption?.length
                  ? periodOption.map(item => {
                      if (thanLastPeriod.includes(compare) && item.value === '60m') {
                        return null
                      } else {
                        return (
                          <Option Option key={item.key} value={item.value}>
                            {_$(item.title)}
                          </Option>
                        )
                      }
                    })
                  : ''}
              </Select>
            </FormItem>
          ) : null
        }
      },
      method: {
        title: _$('统计方法'),
        width: 150,
        template({ itemKey, method }, index) {
          return !withCustomRule.includes(itemKey) && !itemKey?.includes('.nodata') ? (
            <FormItem hideLabel={true} value={method || ''} rules={{ required: true }}>
              <Select
                width={130}
                value={method || ''}
                container=".select_position"
                onChange={value => {
                  changeRules(index, {
                    method: value
                  })
                }}
              >
                {methodOption?.length
                  ? methodOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {_$(item.title)}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          ) : null
        }
      },
      compare: {
        title: _$('大小'),
        width: 180,
        template({ itemKey, period, compare }, index) {
          return !withCustomRule.includes(itemKey) && !itemKey?.includes('.nodata') ? (
            <FormItem hideLabel={true} value={compare || ''} rules={{ required: true }}>
              <Select
                width={160}
                value={compare || ''}
                container=".select_position"
                onChange={value => {
                  // 大小为【周期环比】没有1小时选项
                  changeRules(index, {
                    compare: value,
                    period: thanLastPeriod.includes(value) && period === '60m' ? '' : period
                  })
                }}
              >
                {unitOption?.length
                  ? unitOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {_$(item.title)}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          ) : null
        }
      },
      triggerValue: {
        title: _$('阈值'),
        width: 160,
        template({ itemKey, triggerValue = 0, units, compare }, index) {
          return !withCustomRule.includes(itemKey) && !itemKey?.includes('.nodata') ? (
            <FormItem
              className="table-space"
              hideLabel={true}
              value={triggerValue}
              rules={{ required: true, pointsLimit: true }}
            >
              <Input
                on$change-value={(c, value) =>
                  changeRules(index, {
                    triggerValue: value
                  })
                }
                width={itemKey === 'vpc.nat.ipconflict' ? 120 : 140}
                value={triggerValue}
                b-suffix={
                  // 大小为【周期环比】单位替换成‘%’
                  thanLastPeriod.includes(compare) ? (
                    <React.Fragment>%</React.Fragment>
                  ) : units?.length <= 10 ? (
                    <React.Fragment>{units}</React.Fragment>
                  ) : (
                    <Tooltip content={units}>
                      <Icon className="icon ion-help-circled" />
                    </Tooltip>
                  )
                }
              />

              {itemKey === 'vpc.nat.ipconflict' ? (
                // eslint-disable-next-line max-len
                <Tooltip
                  content={_$(
                    '该计数为NAT时随机选择端口可能产生的冲突计数，仅当统计周期内，大量冲突计数产生，表明NAT端口资源紧张，建议阈值调整为每分钟200'
                  )}
                >
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              ) : null}
            </FormItem>
          ) : null
        }
      },
      effectBT: {
        title: _$('告警生效时间'),
        width: 160,
        template({ itemKey, effectBT, effectET }, index) {
          return !withCustomRule.includes(itemKey) ? (
            <FormItem
              hideLabel={true}
              value={effectBT && effectET ? [effectBT, effectET] : []}
              rules={{ required: true }}
            >
              <Timepicker
                style={{ width: '140px' }}
                value={[effectBT, effectET]}
                onChange={time => {
                  changeRules(index, {
                    effectBT: time[0],
                    effectET: time[1]
                  })
                }}
                step="00:15:00"
                range
                valueFormat="HH:mm"
                showFormat="HH:mm"
              />
            </FormItem>
          ) : null
        }
      },
      points: {
        title: (
          <span>
            {_$('持续周期数')}
            <Tooltip content={_$('连续几次报警的触发结果符合您设置的规则描述，才会触发报警')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ itemKey, period, points }, index) {
          return !withCustomRule.includes(itemKey) ? (
            <FormItem hideLabel={true} value={points || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={points || ''}
                container=".select_position"
                onChange={value => {
                  changeRules(index, {
                    points: value
                  })
                }}
              >
                {pointsOption?.length
                  ? pointsOption
                      .filter(({ value }) => value * parseInt(period) <= 60)
                      .map(item => (
                        <Option key={item.value} value={item.value}>
                          {_$(item.title)}
                        </Option>
                      ))
                  : ''}
              </Select>
            </FormItem>
          ) : null
        }
      },
      interval: {
        title: (
          <span>
            {_$('告警间隔')}
            <Tooltip content={_$('报警发生后如果未恢复正常，间隔多久后重复发送一次报警通知')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ interval }, index) {
          return (
            <FormItem hideLabel={true} value={interval || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={interval || ''}
                container=".select_position"
                onChange={value => {
                  changeRules(index, {
                    interval: value
                  })
                }}
              >
                {intervalOption?.length
                  ? intervalOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {_$(item.title)}
                      </Option>
                    ))
                  : null}
              </Select>
            </FormItem>
          )
        }
      },
      maxCount: {
        title: (
          <span>
            {_$('告警次数')}
            <Tooltip content={_$('报发送告警通知的最大次数')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ maxCount }, index) {
          return (
            <FormItem hideLabel={true} value={maxCount || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={maxCount || ''}
                container=".select_position"
                onChange={value => {
                  changeRules(index, {
                    maxCount: value
                  })
                }}
              >
                {countOption?.length
                  ? countOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {item.title}
                        {_$('次')}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      active: {
        title: _$('操作'),
        fixed: 'right',
        width: 100,
        template(_item, index) {
          return triggerRules.length > 1 ? (
            <a
              onClick={() => {
                removeTriggerRules(index)
              }}
            >
              {_$('删除')}
            </a>
          ) : (
            ''
          )
        }
      }
    }

    return (
      <div className={className}>
        <div className="alarm_rule_title">
          <h1 className="create_model_title">{_$('告警规则')}</h1>
          <p>{_$('满足任意条件可触发')}</p>
          <a
            onClick={() => {
              addTriggerRules()
            }}
          >
            <Icon className="ion-plus-round" />
            {_$('添加规则')}
          </a>
        </div>
        <Form ref={formRef}>
          <Table
            checkType="none"
            data={triggerRules}
            scheme={scheme}
            resizable={true}
            rowKey={item => item.instanceId}
          ></Table>
        </Form>
      </div>
    )
  }
)

export default styled(AlarmRule)`
  .alarm_rule_title {
    display: flex;
    align-items: flex-end;
    padding: 16px 0;
    border-top: 1px solid #f0f0f0;
    margin-top: 16px;
    & > * {
      margin-bottom: 0 !important;
      margin-right: 8px;
    }
    .create_model_title {
      padding: 0 !important;
      margin-top: 0 !important;
      border-top: none !important;
    }
    p {
      color: #999;
      line-height: 12px;
      font-size: 12px;
    }
    a {
      line-height: 12px;
      font-size: 12px;
      .k-icon {
        font-size: 14px;
        margin-right: 4px;
      }
    }
  }
  .k-table {
    margin-top: 16px;
    .ion-help-circled {
      font-size: 14px;
      margin-left: 4px;
    }
    .table-space {
      .k-content > * {
        margin-right: 4px;
      }
    }
    .k-form-item .k-error {
      position: static;
      .c-ellipsis {
        white-space: normal;
      }
    }
  }
`
